import React, { createContext, useContext, useState, useCallback, useEffect } from "react";
import axiosInstance from "../../utils/axiosInstance";
import { useSelector } from "react-redux";
import { getVisitorId, getSessionId } from "../../utils/analytics";

const AssistantChatContext = createContext(null);

export const useAssistantChat = () => {
  const context = useContext(AssistantChatContext);
  if (!context) {
    throw new Error("useAssistantChat must be used within an AssistantChatProvider");
  }
  return context;
};

export const AssistantChatProvider = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [chatThreads, setChatThreads] = useState([]);
  const [activeChatThreadId, setActiveChatThreadId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  
  // Pagination
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isFetchingHistory, setIsFetchingHistory] = useState(false);

  const { isAuthenticated } = useSelector((state) => state.auth);

  const fetchHistory = async (pageNumber, reset = false) => {
    if (isFetchingHistory) return;
    setIsFetchingHistory(true);
    try {
      const token = localStorage.getItem("userToken");
      const response = await axiosInstance.get(`/assistant/chathistory?page=${pageNumber}&limit=20`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const threads = response.data.map(t => ({
        ...t,
        isTemp: false,
        updatedAt: new Date(t.updatedAt).getTime() // standardize to timestamp
      }));
      
      if (reset) {
        setChatThreads(prev => {
          const newThreads = [];
          
          // First, add all threads from server
          threads.forEach(t => {
            const local = prev.find(p => p.id === t.id);
            if (local && local.updatedAt > t.updatedAt) {
              // Local is newer (e.g. message in flight), keep local
              newThreads.push(local);
            } else {
              newThreads.push(t);
            }
          });

          // Then, add any local threads that aren't from the server at all (e.g. new temp threads)
          prev.forEach(p => {
            if (!newThreads.find(nt => nt.id === p.id)) {
              newThreads.push(p);
            }
          });

          return newThreads.sort((a, b) => b.updatedAt - a.updatedAt);
        });
        setPage(1);
      } else {
        setChatThreads(prev => {
          const newThreads = [...prev];
          threads.forEach(t => {
            const existingIdx = newThreads.findIndex(ex => ex.id === t.id);
            if (existingIdx === -1) {
              newThreads.push(t);
            } else if (t.updatedAt > newThreads[existingIdx].updatedAt) {
              newThreads[existingIdx] = t;
            }
          });
          return newThreads.sort((a, b) => b.updatedAt - a.updatedAt);
        });
      }
      
      if (threads.length < 20) {
        setHasMore(false);
      }
    } catch (error) {
      console.error("Failed to fetch chat history", error);
    } finally {
      setIsFetchingHistory(false);
    }
  };

  // Fetch initial history on auth change
  useEffect(() => {
    if (isAuthenticated) {
      fetchHistory(1, true);
    } else {
      setChatThreads([]);
      setActiveChatThreadId(null);
    }
  }, [isAuthenticated]);

  const loadMoreHistory = useCallback(() => {
    if (hasMore && isAuthenticated && !isFetchingHistory) {
      const nextPage = page + 1;
      setPage(nextPage);
      fetchHistory(nextPage, false);
    }
  }, [page, hasMore, isAuthenticated, isFetchingHistory]);

  const createThreadObject = (product) => {
    const isSearchPage = window.location.pathname === "/search";
    const query = isSearchPage ? localStorage.getItem("query") : null;
    
    return {
      id: Date.now().toString() + Math.random().toString(36).substring(2, 9),
      productUrl: product?.url || product?.product_url || null,
      productInfo: product ? {
        title: product?.title || "Product",
        brand: product?.brand || product?.store_name || "",
        image_url: product?.image_url || product?.images?.[0] || null,
      } : null,
      originalQuery: query,
      messages: [],
      updatedAt: Date.now(),
      isTemp: true,
    };
  };

  const openChat = useCallback((product) => {
    if (!isAuthenticated) return;

    if (product) {
      const url = product?.url || product?.product_url || null;
      const existingThread = chatThreads.find(t => t.productUrl === url && !t.isTemp);
      if (existingThread) {
        setActiveChatThreadId(existingThread.id);
      } else {
        const newThread = createThreadObject(product);
        setChatThreads(prev => [...prev.filter(t => !t.isTemp), newThread]);
        setActiveChatThreadId(newThread.id);
      }
    } else {
      if (!activeChatThreadId) {
        const newThread = createThreadObject(null);
        setChatThreads(prev => [...prev.filter(t => !t.isTemp), newThread]);
        setActiveChatThreadId(newThread.id);
      }
      // If there is already an active thread in this session, keep it active
    }
    setIsOpen(true);
  }, [chatThreads, activeChatThreadId, isAuthenticated]);

  const createNewChatThread = useCallback(() => {
    const newThread = createThreadObject(null);
    setChatThreads(prev => [...prev.filter(t => !t.isTemp), newThread]);
    setActiveChatThreadId(newThread.id);
  }, []);

  const switchChatThread = useCallback((id) => {
    const thread = chatThreads.find(t => t.id === id);
    if (thread) {
      setActiveChatThreadId(id);
    }
  }, [chatThreads]);

  const closeChat = useCallback(() => {
    setIsOpen(false);
  }, []);

  const saveThreadToBackend = async (thread) => {
    if (!isAuthenticated) return;
    try {
      const token = localStorage.getItem("userToken");
      const payload = {
        ...thread,
        visitor_id: getVisitorId(),
        session_id: getSessionId()
      };
      await axiosInstance.post('/assistant/chathistory', payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      console.error("Failed to save chat to backend", error);
    }
  };

  const addMessage = useCallback((threadId, role, text, products = null, search_query = null) => {
    const newMessage = { role, text, products, search_query, timestamp: Date.now() };

    setChatThreads(prev => {
      let updatedThread = null;
      const newThreads = prev.map(thread => {
        if (thread.id === threadId) {
          updatedThread = {
            ...thread,
            messages: [...thread.messages, newMessage],
            updatedAt: Date.now(),
            isTemp: false,
          };
          return updatedThread;
        }
        return thread;
      });

      if (updatedThread) {
        // Fire and forget save
        saveThreadToBackend(updatedThread);
      }

      return newThreads;
    });
  }, [isAuthenticated]);

  const deleteThread = useCallback(async (id) => {
    try {
      const threadToDelete = chatThreads.find(t => t.id === id);
      
      if (activeChatThreadId === id) {
        const newThread = createThreadObject(null);
        setChatThreads(prev => [...prev.filter(t => t.id !== id && !t.isTemp), newThread]);
        setActiveChatThreadId(newThread.id);
      } else {
        setChatThreads(prev => prev.filter(t => t.id !== id));
      }
      
      if (isAuthenticated && threadToDelete && !threadToDelete.isTemp) {
        const token = localStorage.getItem("userToken");
        await axiosInstance.delete(`/assistant/chathistory/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
    } catch (error) {
      console.error("Failed to delete chat", error);
      // We could optionally refetch history here if it failed
    }
  }, [activeChatThreadId, isAuthenticated, chatThreads]);

  const activeThread = chatThreads.find(t => t.id === activeChatThreadId) || null;

  const value = {
    isOpen,
    chatThreads,
    activeChatThreadId,
    activeThread, 
    isLoading,
    setIsLoading,
    hasMore,
    isFetchingHistory,
    openChat,
    closeChat,
    addMessage,
    createNewChatThread,
    switchChatThread,
    loadMoreHistory,
    deleteThread
  };

  return (
    <AssistantChatContext.Provider value={value}>
      {children}
    </AssistantChatContext.Provider>
  );
};

export default AssistantChatContext;
